<script setup lang="ts">
import { useErrorHandler } from '@errors/useErrorHandler';
import { type Contactpersoon, getTitulatuurOptions } from '@relatie-lib/relatieGegevens/types';
import { CrmRelationsRelatieContext } from '@services/crm-public/orval-api/model/crmRelationsRelatieContext';
import { SUREbusinessCommonDomainRelatiesTitulatuurcodeType as Titulatuurcode } from '@services/crm-public/orval-api/model/sUREbusinessCommonDomainRelatiesTitulatuurcodeType';
import {
  useRelatieContactpersonenMaakRelatieContactPersoon,
  useRelatieContactpersonenMuteerRelatieContactPersoon,
} from '@services/crm-public/orval-api/relatie-contactpersonen-api/relatie-contactpersonen-api';
import FieldGroup from '@shared/form/FieldRowV2/FieldGroup.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import VDatePicker from '@shared/form/VDatePicker/VDatePicker.vue';
import VInput from '@shared/form/VInput/VInput.vue';
import VSelect from '@shared/form/VSelect/VSelect.vue';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { usePhoneNumberValidator } from '@shared/validation/usePhoneNumberValidator';
import { toDateonly } from '@tools/dates';
import { deepClone } from '@tools/helpers';
import { v4 as uuidv4 } from 'uuid';
import { computed, ref, toRefs, watch, watchEffect } from 'vue';
import { z } from 'zod';
import { type ContactpersoonEditMode } from './types';

const props = defineProps<{ isReadonly?: boolean; relatienummer: number }>();

const { relatienummer } = toRefs(props);

const contactpersoon = defineModel<ContactpersoonEditMode>('modelValue', { required: true });

const { handleError } = useErrorHandler();
const notification = useNotification();

const uuid = uuidv4();

const telefoonnummerMaxLength = 18;
const emailMaxLength = 63;
const voornaamMaxLength = 20;
const voorlettersMaxLength = 10;
const voorvoegselsMaxLength = 10;
const achternaamMaxLength = 32;

const { schema: phoneNumberSchema } = usePhoneNumberValidator({
  maxLength: telefoonnummerMaxLength,
});

const originalValues = ref<ContactpersoonEditMode>();

const geboortedatumRequired = computed(
  () => !!originalValues.value?.geboortedatum && contactpersoon.value.inEditMode,
);

const schema = computed(() =>
  z.object({
    email: z.string().email().max(emailMaxLength),
    telefoonnummer: phoneNumberSchema.value,
    geboortedatum: geboortedatumRequired.value
      ? z.date().max(new Date())
      : z.date().max(new Date()).nullable().optional(),
    titulatuurcode: z.nativeEnum(Titulatuurcode, { required_error: 'Aanhef is verplicht' }),
    voornaam: z.string().max(voornaamMaxLength).nullable().optional().or(z.literal('')),
    voorletters: z.string().max(voorlettersMaxLength).nullable().optional().or(z.literal('')),
    voorvoegsels: z.string().max(voorvoegselsMaxLength).nullable().optional().or(z.literal('')),
    naam: z.string().max(achternaamMaxLength).min(1, { message: 'Achternaam is verplicht' }),
  }),
);

const validationResult = ref();

const isDisabled = ref<boolean>();

defineExpose({ save: () => save() });

function onError(err: any) {
  handleError(err);
  isDisabled.value = false;
}

function onSuccess() {
  isDisabled.value = false;
  originalValues.value = undefined;
  contactpersoon.value.inEditMode = false;
  notification.success('Wijzigingen opgeslagen.');
}

function onCreated(res: string) {
  contactpersoon.value.id = res;
  onSuccess();
}

const { mutate, isPending: isMutating } = useRelatieContactpersonenMuteerRelatieContactPersoon({
  mutation: {
    onError,
    onSuccess,
  },
});

const { mutate: create, isPending: isCreating } =
  useRelatieContactpersonenMaakRelatieContactPersoon({
    mutation: {
      onError,
      onSuccess: onCreated,
    },
  });

const isLoading = computed(() => isMutating.value || isCreating.value);

watchEffect(() => (contactpersoon.value.isLoading = isLoading.value));

watch(
  () => contactpersoon.value.inEditMode,
  (val) => {
    if (val) originalValues.value = deepClone(contactpersoon.value);
    if (!val && originalValues.value) {
      validationResult.value = undefined;
      contactpersoon.value = deepClone({ ...originalValues.value, inEditMode: false });
    }
  },
);

function save() {
  validationResult.value = schema.value.safeParse(contactpersoon.value);

  if (!validationResult.value.success) return;

  isDisabled.value = true;

  const dto = {
    ...contactpersoon.value,
    geboortedatum: contactpersoon.value.geboortedatum
      ? toDateonly(contactpersoon.value.geboortedatum)
      : undefined,
    titulatuurcode: contactpersoon.value.titulatuurcode!,
    relatieContext: CrmRelationsRelatieContext.Algemeen,
    jeVorm: true,
  };

  if (contactpersoon.value.id && contactpersoon.value.id.length > 0) {
    mutate({
      relatienummer: relatienummer.value,
      contactpersoonId: contactpersoon.value.id,
      data: dto,
    });
  } else {
    create({ relatienummer: relatienummer.value, data: dto });
  }
}

function fieldError(field: keyof Contactpersoon) {
  if (!validationResult.value || validationResult.value.success) return [];

  const issues = validationResult.value.error?.issues || [];

  const fieldIssues = issues.filter((issue: any) => issue.path[0] === field);

  return fieldIssues.map((issue: any) => issue.message);
}

const voornaam = computed(() =>
  contactpersoon.value.voorletters && contactpersoon.value.voornaam
    ? `(${contactpersoon.value.voornaam})`
    : contactpersoon.value.voornaam,
);
</script>

<template>
  <FieldGroup :class="{ '!gap-0': props.isReadonly }">
    <FieldRowV2
      :errors="fieldError('titulatuurcode')"
      label="Aanhef"
      label-for="Aanhef"
      :required="!props.isReadonly"
    >
      <VSelect
        class="flex-grow basis-32"
        v-model="contactpersoon.titulatuurcode"
        :options="getTitulatuurOptions(contactpersoon.titulatuurcode)"
        option-label="label"
        option-value="value"
        :readonly="props.isReadonly"
        placeholder="Aanhef"
        :disabled="isDisabled"
        id="Aanhef"
      />
    </FieldRowV2>
    <FieldRowV2
      :required="!props.isReadonly"
      label="Naam"
      :errors="[
        ...fieldError('naam'),
        ...fieldError('voornaam'),
        ...fieldError('voorletters'),
        ...fieldError('voorvoegsels'),
      ]"
    >
      <div v-if="props.isReadonly">
        {{ contactpersoon.voorletters }}
        {{ voornaam }}
        {{ contactpersoon.voorvoegsels }} {{ contactpersoon.naam }}
      </div>
      <div v-else class="flex flex-grow flex-wrap gap-4">
        <VInput
          v-model="contactpersoon.voornaam"
          class="flex-grow basis-24"
          :has-error="fieldError('voornaam').length > 0"
          :is-disabled="isDisabled"
          is-required
          :max-length="voornaamMaxLength"
          :id="`voornaam`"
          placeholder="Voornaam"
        />

        <VInput
          v-model="contactpersoon.voorletters"
          class="flex-grow basis-24"
          :has-error="fieldError('voorletters').length > 0"
          :is-disabled="isDisabled"
          :max-length="voorlettersMaxLength"
          :id="`voorletters`"
          placeholder="Voorletters"
        />

        <VInput
          v-model="contactpersoon.voorvoegsels"
          class="flex-grow basis-24"
          :has-error="fieldError('voorvoegsels').length > 0"
          :is-disabled="isDisabled"
          :max-length="voorvoegselsMaxLength"
          :id="`voorvoegsels`"
          placeholder="Tussenvoegsel"
        />

        <VInput
          v-model="contactpersoon.naam"
          class="flex-grow basis-60"
          :has-error="fieldError('naam').length > 0"
          :is-disabled="isDisabled"
          :max-length="achternaamMaxLength"
          :id="`naam`"
          placeholder="Achternaam"
        />
      </div>
    </FieldRowV2>

    <FieldRowV2
      :errors="fieldError('geboortedatum')"
      label="Geboortedatum"
      :label-for="`geboortedatum_${uuid}`"
      :required="geboortedatumRequired"
    >
      <VDatePicker
        v-model="contactpersoon.geboortedatum"
        :max-date="new Date()"
        :disabled="isDisabled"
        :readonly="props.isReadonly"
        :invalid="fieldError('geboortedatum').length > 0"
        :id="`geboortedatum_${uuid}`"
      />
    </FieldRowV2>

    <FieldRowV2
      :errors="fieldError('email')"
      :label-for="`email_${uuid}`"
      label="E-mailadres"
      :required="!props.isReadonly"
    >
      <VInput
        v-model="contactpersoon.email"
        :has-error="fieldError('email').length > 0"
        :id="`email_${uuid}`"
        :is-disabled="isDisabled"
        :is-readonly="props.isReadonly"
        is-required
        :max-length="emailMaxLength"
        type="email"
      />
    </FieldRowV2>

    <FieldRowV2
      :errors="fieldError('telefoonnummer')"
      :label-for="`telefoonnummer_${uuid}`"
      label="Telefoonnummer"
    >
      <VInput
        v-model="contactpersoon.telefoonnummer"
        :id="`telefoonnummer_${uuid}`"
        :is-disabled="isDisabled"
        :is-readonly="props.isReadonly"
        is-required
        :max-length="telefoonnummerMaxLength"
        type="tel"
      />
    </FieldRowV2>
  </FieldGroup>
</template>
